// A tab left open keeps running the bundle it booted with: index.html is served
// must-revalidate, but revalidation only happens on a page load, which a backgrounded tab
// never performs. The webUpdateNotice plugin (vite.config.ts) injects a script that polls the
// deployed version against this build's and, because we set hiddenDefaultNotification,
// dispatches an event instead of rendering its own prompt.
//
// Reloading the moment that event arrives is what made this expensive: it lands in the middle
// of whatever the user was doing, for a build they had no reason to care about yet. So the
// event only records the staleness, and the reload waits for the next in-app route change —
// where the page is being replaced anyway, a document load is hard to tell from a client-side
// transition, and nothing is discarded that the navigation wasn't already discarding.
//
// Waiting costs nothing, because a route change is also the only thing a stale tab can't do.
// Every code-split chunk in this app is a lazy route (routes.tsx), and `s3 sync --delete` means
// the old build's chunks are gone from the origin — so the two coincide exactly: the navigation
// that would 404 is the navigation that reloads. Both start at once and either order ends in
// the same place, the reload replacing the document or the failed import doing it below.
//
// A backgrounded tab is deliberately not one of those moments. Reloading while nobody is
// watching sounds free, but the half-filled form someone tabbed away from is exactly what they
// come back to.
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { isNavigationHeld } from './navigationHold';

// The plugin's CUSTOM_UPDATE_EVENT_NAME, dispatched on document.body. Hardcoded because the
// vite package doesn't re-export it.
const UPDATE_EVENT = 'plugin_web_update_notice';

// Reload at most once per newly deployed version, remembered for the life of the tab. Guards
// against a mismatch that reloading cannot resolve — a stale index.html held at a CloudFront
// edge against a fresh version.json — which would otherwise reload on every route change for
// as long as the tab stays open. Written when the reload is performed rather than when the
// staleness is noticed, because a version we noticed but never acted on has to stay actionable.
const RELOADED_FOR_KEY = 'staleBuildReloadedFor';

// The equivalent for a reload a missing chunk asked for, where there is no version to key on.
// A time window rather than a once-per-tab flag: the same tab can meet a second deploy hours
// later and deserves a second reload, while a chunk that is still missing right after one has
// established that reloading is not the answer.
const CHUNK_RELOAD_AT_KEY = 'staleBuildChunkReloadAt';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

// The deployed version this tab knows it isn't running, or null while it is current. Module
// state: a reload is what clears it.
let staleVersion: string | null = null;

export function reloadOnStaleBuild(): void {
  document.body.addEventListener(UPDATE_EVENT, (event) => {
    const version = (event as CustomEvent<{ version?: string }>).detail?.version;
    if (!version) return;
    staleVersion = version;
  });

  // A dynamic import whose chunk the origin no longer has — the stale tab's one hard failure,
  // and the backstop for a route change that fetched faster than the reload below could start
  // (or for a deploy the version poll hasn't reported yet). Reloading here isn't an
  // interruption: the route the user asked for cannot render at all, so this is the only way it
  // renders. Handled centrally because every lazy route fails the same way.
  window.addEventListener('vite:preloadError', (event) => {
    // Decided before preventDefault, so a failure we are not going to act on still reaches
    // Vite's rethrow, the global handler and PostHog rather than leaving a silently empty route.
    if (isNavigationHeld() || !claimChunkReload()) return;
    event.preventDefault();
    window.location.reload();
  });
}

// Take the new build if this tab is on an old one. Called where the page is about to be replaced
// regardless (see StaleBuildReloader); a no-op otherwise.
export function reloadIfBuildStale(): void {
  const version = staleVersion;
  if (!version) return;

  // A live chat does not survive the document being replaced (see navigationHold). Unlike the
  // re-auth redirect, this has other chances: stay on the old build and take the next one.
  if (isNavigationHeld()) return;

  // Either way this version has had its turn — the reload lands us on the new build, or
  // reloading can't fix the mismatch and retrying on every route change would be a loop.
  staleVersion = null;

  try {
    if (sessionStorage.getItem(RELOADED_FOR_KEY) === version) return;
    sessionStorage.setItem(RELOADED_FOR_KEY, version);
  } catch {
    // sessionStorage blocked (private mode, storage partitioning). Reload anyway — the loop
    // guard is a safety net, not a precondition for getting the user onto the new build.
  }

  window.location.reload();
}

// React.lazy for a code-split route; every lazy import in the app goes through this. Preventing
// vite:preloadError (above) stops Vite rethrowing, and its preload helper then resolves the import
// to undefined instead — which React.lazy would read `.default` from and throw on, reporting an
// uncaught TypeError for a route that is in fact being recovered. Undefined only ever means that
// reload is under way, so the import is left pending and Suspense's fallback stays up until the
// document is replaced.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.lazy's own constraint
export function lazyRoute<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => load().then((module) => module ?? new Promise<never>(() => {})));
}

// Whether a missing chunk may reload now, claiming the slot if so.
function claimChunkReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY)) || 0;
    if (last && Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // As above: without storage there is no loop guard, and getting the user onto a build that
    // can render the route they asked for matters more than the guard.
  }
  return true;
}
